/**
 * Template Model
 * Book and chapter templates for quick start
 */

export interface ChapterTemplate {
  title: string
  content: string
  order: number
}

export interface BookTemplate {
  id: string
  name: string
  description: string
  genre: string[]
  category: 'fiction' | 'non-fiction' | 'educational' | 'business' | 'creative'
  icon: string
  chapters: ChapterTemplate[]
  metadata?: {
    recommendedLength?: string
    targetAudience?: string
    tips?: string[]
  }
}

/**
 * Pre-defined book templates
 */
export const BOOK_TEMPLATES: BookTemplate[] = [
  {
    id: 'blank',
    name: 'Blank Book',
    description: 'Start from scratch with no pre-filled content',
    genre: ['General'],
    category: 'creative',
    icon: '📝',
    chapters: [],
  },
  {
    id: 'novel',
    name: 'Novel',
    description: 'Classic fiction novel structure with key story elements',
    genre: ['Fiction'],
    category: 'fiction',
    icon: '📚',
    chapters: [
      {
        title: 'Chapter 1: The Beginning',
        content: '<p><strong>Opening Scene</strong></p><p>Introduce your protagonist, setting, and the ordinary world before the adventure begins...</p>',
        order: 0,
      },
      {
        title: 'Chapter 2: The Inciting Incident',
        content: '<p>The event that disrupts the ordinary world and launches the story...</p>',
        order: 1,
      },
      {
        title: 'Chapter 3: Rising Action',
        content: '<p>Complications and obstacles begin to mount...</p>',
        order: 2,
      },
      {
        title: 'Chapter 4: Midpoint',
        content: '<p>A major revelation or turning point that changes everything...</p>',
        order: 3,
      },
      {
        title: 'Chapter 5: Climax',
        content: '<p>The peak of tension where everything comes to a head...</p>',
        order: 4,
      },
      {
        title: 'Chapter 6: Resolution',
        content: '<p>Wrap up loose ends and show the new normal...</p>',
        order: 5,
      },
    ],
    metadata: {
      recommendedLength: '50,000 - 100,000 words',
      targetAudience: 'General fiction readers',
      tips: [
        'Develop compelling characters with clear motivations',
        'Create tension and conflict in every chapter',
        'Use dialogue to reveal character and advance plot',
      ],
    },
  },
  {
    id: 'memoir',
    name: 'Memoir',
    description: 'Personal story template with thematic structure',
    genre: ['Biography', 'Non-Fiction'],
    category: 'non-fiction',
    icon: '✍️',
    chapters: [
      {
        title: 'Introduction: Why This Story Matters',
        content: '<p>Explain what readers will gain from your story and why it\'s important to share...</p>',
        order: 0,
      },
      {
        title: 'Part 1: The Beginning',
        content: '<p>Describe your early experiences that shaped who you became...</p>',
        order: 1,
      },
      {
        title: 'Part 2: The Turning Point',
        content: '<p>The pivotal moment or period that changed everything...</p>',
        order: 2,
      },
      {
        title: 'Part 3: The Journey',
        content: '<p>Navigate through challenges and growth...</p>',
        order: 3,
      },
      {
        title: 'Part 4: Lessons Learned',
        content: '<p>Reflect on what the experiences taught you...</p>',
        order: 4,
      },
      {
        title: 'Conclusion: Looking Forward',
        content: '<p>Share your perspective now and hopes for the future...</p>',
        order: 5,
      },
    ],
    metadata: {
      recommendedLength: '40,000 - 80,000 words',
      targetAudience: 'Readers interested in personal stories',
      tips: [
        'Be honest and vulnerable',
        'Focus on universal themes readers can relate to',
        'Include sensory details to bring memories to life',
      ],
    },
  },
  {
    id: 'how-to-guide',
    name: 'How-To Guide',
    description: 'Educational guide with step-by-step structure',
    genre: ['Self-Help', 'Non-Fiction'],
    category: 'educational',
    icon: '🎓',
    chapters: [
      {
        title: 'Introduction: What You\'ll Learn',
        content: '<p>Overview of the skills or knowledge readers will gain...</p><ul><li>Key benefit 1</li><li>Key benefit 2</li><li>Key benefit 3</li></ul>',
        order: 0,
      },
      {
        title: 'Chapter 1: Getting Started',
        content: '<p>Foundation and prerequisites...</p><h3>What You Need</h3><p>List materials, tools, or knowledge required...</p>',
        order: 1,
      },
      {
        title: 'Chapter 2: Step-by-Step Process',
        content: '<p>Detailed instructions for the main process...</p><ol><li>First step</li><li>Second step</li><li>Third step</li></ol>',
        order: 2,
      },
      {
        title: 'Chapter 3: Common Mistakes',
        content: '<p>Pitfalls to avoid and how to overcome them...</p>',
        order: 3,
      },
      {
        title: 'Chapter 4: Advanced Techniques',
        content: '<p>Take your skills to the next level...</p>',
        order: 4,
      },
      {
        title: 'Conclusion: Next Steps',
        content: '<p>Where to go from here and additional resources...</p>',
        order: 5,
      },
    ],
    metadata: {
      recommendedLength: '20,000 - 50,000 words',
      targetAudience: 'Learners seeking practical knowledge',
      tips: [
        'Use clear, simple language',
        'Include examples and case studies',
        'Add visual aids and diagrams if possible',
      ],
    },
  },
  {
    id: 'business-book',
    name: 'Business Book',
    description: 'Professional development or business strategy book',
    genre: ['Business', 'Non-Fiction'],
    category: 'business',
    icon: '💼',
    chapters: [
      {
        title: 'Introduction: The Problem',
        content: '<p>Identify the business challenge or opportunity...</p>',
        order: 0,
      },
      {
        title: 'Chapter 1: The Framework',
        content: '<p>Present your methodology or approach...</p><h3>The Three Pillars</h3><ol><li>Pillar 1</li><li>Pillar 2</li><li>Pillar 3</li></ol>',
        order: 1,
      },
      {
        title: 'Chapter 2: Case Studies',
        content: '<p>Real-world examples of the framework in action...</p>',
        order: 2,
      },
      {
        title: 'Chapter 3: Implementation',
        content: '<p>Practical steps to apply these concepts...</p>',
        order: 3,
      },
      {
        title: 'Chapter 4: Measuring Success',
        content: '<p>Key metrics and indicators of progress...</p>',
        order: 4,
      },
      {
        title: 'Conclusion: Taking Action',
        content: '<p>Your action plan for the next 30, 60, 90 days...</p>',
        order: 5,
      },
    ],
    metadata: {
      recommendedLength: '30,000 - 60,000 words',
      targetAudience: 'Business professionals and entrepreneurs',
      tips: [
        'Back claims with data and research',
        'Include actionable takeaways',
        'Use frameworks and models to organize information',
      ],
    },
  },
  {
    id: 'cookbook',
    name: 'Cookbook',
    description: 'Recipe collection with organized categories',
    genre: ['Other'],
    category: 'creative',
    icon: '🍳',
    chapters: [
      {
        title: 'Introduction: My Culinary Journey',
        content: '<p>Share your cooking philosophy and what makes these recipes special...</p>',
        order: 0,
      },
      {
        title: 'Chapter 1: Appetizers',
        content: '<h3>Recipe Name</h3><p><strong>Ingredients:</strong></p><ul><li>Ingredient 1</li><li>Ingredient 2</li></ul><p><strong>Instructions:</strong></p><ol><li>Step 1</li><li>Step 2</li></ol>',
        order: 1,
      },
      {
        title: 'Chapter 2: Main Courses',
        content: '<p>Your signature dishes and family favorites...</p>',
        order: 2,
      },
      {
        title: 'Chapter 3: Side Dishes',
        content: '<p>Perfect accompaniments to complete any meal...</p>',
        order: 3,
      },
      {
        title: 'Chapter 4: Desserts',
        content: '<p>Sweet endings to delight your guests...</p>',
        order: 4,
      },
      {
        title: 'Chapter 5: Tips & Techniques',
        content: '<p>Essential cooking tips and kitchen hacks...</p><blockquote>Pro tip: Always taste as you go!</blockquote>',
        order: 5,
      },
    ],
    metadata: {
      recommendedLength: '15,000 - 40,000 words',
      targetAudience: 'Home cooks and food enthusiasts',
      tips: [
        'Be precise with measurements',
        'Include prep and cook times',
        'Add photos of finished dishes if possible',
      ],
    },
  },
  {
    id: 'short-story-collection',
    name: 'Short Story Collection',
    description: 'Collection of standalone short stories',
    genre: ['Fiction'],
    category: 'fiction',
    icon: '📖',
    chapters: [
      {
        title: 'Foreword',
        content: '<p>Introduce the themes that connect these stories...</p>',
        order: 0,
      },
      {
        title: 'Story 1: [Title]',
        content: '<p>Your first short story...</p>',
        order: 1,
      },
      {
        title: 'Story 2: [Title]',
        content: '<p>Your second short story...</p>',
        order: 2,
      },
      {
        title: 'Story 3: [Title]',
        content: '<p>Your third short story...</p>',
        order: 3,
      },
      {
        title: 'Afterword',
        content: '<p>Reflect on the writing process and thank your readers...</p>',
        order: 4,
      },
    ],
    metadata: {
      recommendedLength: '20,000 - 40,000 words',
      targetAudience: 'Short fiction enthusiasts',
      tips: [
        'Each story should work as a standalone piece',
        'Consider a unifying theme or setting',
        'Vary the length and tone of stories',
      ],
    },
  },
]
